import { type ReactNode, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import styles from "./Tabs.module.css";
// @ts-expect-error pure js
import { classnames } from "../../../utils";
import Text from "../Text.tsx";

interface TabsProps {
  /** URL mode: the active tab is stored in the `tab-${id}` search param. */
  id?: string;
  defaultValue?: string;
  /** Controlled mode: pass value + onValueChange instead of id (e.g. inside a modal). */
  value?: string;
  onValueChange?: (value: string) => void;
  children: ReactNode;
  className?: string;
}

interface TabsListProps {
  children: ReactNode;
  className?: string;
}

interface TabProps {
  value: string;
  children: ReactNode;
  className?: string;
  /** Ancre pour les guides de première visite (ciblée via [data-tour='...']). */
  dataTour?: string;
}

interface TabPanelProps {
  value: string;
  children: ReactNode;
  className?: string;
  keepMounted?: boolean;
}

export function Tabs({
  id,
  defaultValue,
  value,
  onValueChange,
  children,
  className,
}: TabsProps) {
  const [params, setParams] = useSearchParams();
  const currentTab = value ?? params.get(`tab-${id}`);

  const handleValueChange = useCallback(
    (newValue: string | null) => {
      if (newValue === null) return;
      if (onValueChange) {
        onValueChange(newValue);
        return;
      }
      setParams((cur) => {
        const newParams = new URLSearchParams(cur.toString());
        newParams.set(`tab-${id}`, newValue);
        return newParams;
      });
    },
    [id, setParams, onValueChange],
  );

  return (
    <BaseTabs.Root
      value={currentTab ?? defaultValue}
      onValueChange={handleValueChange}
      className={className}
    >
      {children}
    </BaseTabs.Root>
  );
}

export function TabsList({ children, className }: TabsListProps) {
  return (
    <BaseTabs.List className={classnames(styles.list, className)}>
      {children}
    </BaseTabs.List>
  );
}

export function Tab({ value, children, className, dataTour }: TabProps) {
  return (
    <BaseTabs.Tab
      value={value}
      className={classnames(styles.tab, className)}
      nativeButton={false}
      data-tour={dataTour}
      render={<Text as="div">{children}</Text>}
    />
  );
}

export function TabPanel({
  value,
  children,
  className,
  keepMounted,
}: TabPanelProps) {
  return (
    <BaseTabs.Panel
      value={value}
      keepMounted={keepMounted}
      className={classnames(styles.panel, className)}
    >
      {children}
    </BaseTabs.Panel>
  );
}

Tabs.List = TabsList;
Tabs.Tab = Tab;
Tabs.Panel = TabPanel;
